import {
	HStack,
	IconAdd,
	IconButton,
	IconCross,
	Input,
	Text,
	VStack,
} from '@montel/montelpro-shared-components-v2';
import { FunctionComponent, useState } from 'react';
import { styled } from 'styled-components';

const StyledPlusButton = styled(IconButton)`
	align-self: flex-start;
	margin-top: 8px;
`;

const MultiInput: FunctionComponent<{
	className?: string;
	values: string[];
	setValue: (value: string[]) => void;
	label: string;
	inputSize?: 'sm' | 'md';
	labelSize?: 'xs' | 'sm';
	validateFn?: (value: string) => string | undefined;
	error: string | undefined;
	setError: (error: string | undefined) => void;
}> = ({
	className,
	values,
	setValue,
	label,
	inputSize = 'md',
	labelSize = 'sm',
	validateFn,
	error,
	setError,
}) => {
	const [newItem, setNewItem] = useState<string>('');

	const onAddValue = (value: string) => {
		if (validateFn) {
			const error = validateFn(value);
			if (error) {
				setError(error);
				return;
			}
		}
		setValue([...values, value]);
		setNewItem('');
	};

	const handleKeyDown = (e: any) => {
		if (e.key === 'Enter' && newItem) {
			e.preventDefault();

			if (validateFn) {
				const validationError = validateFn(newItem);
				if (validationError) {
					setError(validationError);
					return;
				}
			}

			if (values.includes(newItem)) {
				setError('This domain is already added.');
				return;
			}

			onAddValue(newItem);
		}
	};

	const removeValue = (index: number) => {
		setValue([...values.slice(0, index), ...values.slice(index + 1)]);
	};

	const onNewItemChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setNewItem(e.target.value);

		if (error) {
			setError(undefined);
		}
	};

	const onBlur = () => {
		if (validateFn) {
			setError(validateFn(newItem));
		}
	};

	return (
		<VStack className={className}>
			<Text color="textSubtle" size={labelSize}>
				{label}
			</Text>
			{values.map((value, i) => (
				<HStack
					key={i}
					wrap="nowrap"
					justify="space-between"
					align="center"
					fullWidth
				>
					<Text size={inputSize} color="textDefault">
						{value}
					</Text>
					<IconButton
						size="sm"
						variant="tertiary"
						icon={<IconCross />}
						onClick={() => {
							removeValue(i);
						}}
					/>
				</HStack>
			))}
			<VStack gap="xs" fullWidth>
				<HStack
					wrap="nowrap"
					align="center"
					justify="space-between"
					fullWidth
				>
					<Input
						label="New item"
						size={inputSize}
						hideLabel
						placeholder="Type new domain..."
						value={newItem}
						onChange={onNewItemChange}
						onBlur={onBlur}
						errorMessage={error}
						onKeyPress={handleKeyDown}
					/>
					<StyledPlusButton
						icon={<IconAdd />}
						size="sm"
						onClick={() => {
							if (newItem) {
								onAddValue(newItem);
							}
						}}
					/>
				</HStack>
			</VStack>
		</VStack>
	);
};

export default MultiInput;
